import React from 'react';
import { motion } from 'framer-motion';

// Reusable geometric shape components
export const Triangle = ({ size = 'md', color = 'primary', className = '', rotation = 0 }) => {
  const sizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const colors = {
    primary: 'text-primary-500',
    accent: 'text-accent-500',
    blue: 'text-geometric-blue',
    green: 'text-geometric-green',
    yellow: 'text-geometric-yellow',
    purple: 'text-geometric-purple',
    neutral: 'text-neutral-400'
  };

  return (
    <div 
      className={`${sizes[size]} ${colors[color]} ${className}`}
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2 L22 20 L2 20 Z" />
      </svg>
    </div>
  );
};

export const Circle = ({ size = 'md', color = 'primary', className = '', filled = false }) => {
  const sizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const colors = {
    primary: 'text-primary-500',
    accent: 'text-accent-500',
    blue: 'text-geometric-blue',
    green: 'text-geometric-green',
    yellow: 'text-geometric-yellow',
    purple: 'text-geometric-purple',
    neutral: 'text-neutral-400'
  };

  return (
    <div className={`${sizes[size]} ${colors[color]} ${className}`}>
      <svg viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
      </svg>
    </div>
  );
};

export const Square = ({ size = 'md', color = 'primary', className = '', rotation = 0 }) => {
  const sizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const colors = {
    primary: 'text-primary-500',
    accent: 'text-accent-500',
    blue: 'text-geometric-blue',
    green: 'text-geometric-green',
    yellow: 'text-geometric-yellow',
    purple: 'text-geometric-purple',
    neutral: 'text-neutral-400'
  };

  return (
    <div 
      className={`${sizes[size]} ${colors[color]} ${className}`}
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" />
      </svg>
    </div>
  );
};

export const Hexagon = ({ size = 'md', color = 'primary', className = '' }) => {
  const sizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const colors = {
    primary: 'text-primary-500',
    accent: 'text-accent-500',
    blue: 'text-geometric-blue',
    green: 'text-geometric-green',
    yellow: 'text-geometric-yellow',
    purple: 'text-geometric-purple',
    neutral: 'text-neutral-400'
  };

  return (
    <div className={`${sizes[size]} ${colors[color]} ${className}`}>
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.5 3.5L22 12l-4.5 8.5h-11L2 12l4.5-8.5h11z" />
      </svg>
    </div>
  );
};

// Geometric pattern background component
export const GeometricBackground = ({ children, pattern = 'grid', className = '' }) => {
  const patterns = {
    grid: 'bg-geometric-pattern',
    dots: "bg-[radial-gradient(circle_at_1px_1px,rgba(98,125,152,0.1)_1px,transparent_0)] bg-[length:20px_20px]",
    diagonal: "bg-[linear-gradient(45deg,transparent_35%,rgba(98,125,152,0.05)_35%,rgba(98,125,152,0.05)_65%,transparent_65%)] bg-[length:20px_20px]"
  };

  return (
    <div className={`relative ${patterns[pattern]} ${className}`}>
      {children}
    </div>
  );
};

// Full screen Geometric Shapes Background Component
export const GeometricShapes = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      <GeometricBackground pattern="dots" className="absolute inset-0 opacity-50" />
      
      <motion.div 
        className="absolute top-10 left-10 opacity-20"
        animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      >
        <Triangle size="xl" color="blue" rotation={15} />
      </motion.div>

      <motion.div 
        className="absolute top-20 right-20 opacity-20"
        animate={{ y: [0, 20, 0], scale: [1, 1.1, 1] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      >
        <Circle size="lg" color="green" />
      </motion.div>

      <motion.div 
        className="absolute bottom-20 left-1/4 opacity-20"
        animate={{ y: [0, -15, 0], rotate: [45, 60, 45] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      >
        <Square size="xl" color="purple" rotation={45} />
      </motion.div>

      <motion.div 
        className="absolute bottom-10 right-10 opacity-20"
        animate={{ y: [0, 25, 0], rotate: [0, -10, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      >
        <Hexagon size="lg" color="yellow" />
      </motion.div>

      <motion.div 
        className="absolute top-1/2 right-1/3 opacity-10"
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <Circle size="xl" color="primary" filled />
      </motion.div>
    </div>
  );
};